<div class="container-fluid py-3">
  <!-- Header -->
  <div class="d-flex justify-content-between align-items-center mb-3">
    <h5 class="mb-0">
      <i class="bi bi-robot me-2"></i>{{ 'KAIPrompts.title' | translate }}
    </h5>
  </div>

  <!-- Filters -->
  <div class="row g-2 mb-3">
    <div class="col-md-3">
      <select class="form-select form-select-sm" [(ngModel)]="selectedModule" (ngModelChange)="onModuleChange()">
        <option value="">{{ 'KAIPrompts.allModules' | translate }}</option>
        <option *ngFor="let mod of modules" [value]="mod">{{ mod }}</option>
      </select>
    </div>
    <div class="col-md-4">
      <input type="text" class="form-control form-control-sm"
             [placeholder]="'KAIPrompts.searchPlaceholder' | translate"
             [(ngModel)]="searchTerm"
             (ngModelChange)="onSearchChange()" />
    </div>
    <div class="col-md-5 text-end text-muted small align-self-center">
      {{ filteredPrompts.length }} / {{ prompts.length }} {{ 'KAIPrompts.prompts' | translate }}
    </div>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="text-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">{{ 'General.loading' | translate }}</span>
    </div>
  </div>

  <!-- Prompt List -->
  <div *ngIf="!loading" class="prompt-list">
    <div *ngFor="let prompt of filteredPrompts" class="card mb-2 prompt-card">
      <div class="card-body py-2 px-3">
        <div class="d-flex justify-content-between align-items-start">
          <div class="flex-grow-1 me-3">
            <div class="d-flex align-items-center gap-2 mb-1">
              <span class="fw-semibold text-primary">{{ getShortKey(prompt.promptKey) }}</span>
              <span class="badge badge-soft-dark bg-opacity-10 text-dark small">{{ prompt.module }}</span>
              <span *ngIf="prompt.outputFormat" class="badge small" [ngClass]="getOutputFormatBadgeClass(prompt.outputFormat)">
                {{ prompt.outputFormat }}
              </span>
              <span class="badge badge-soft-success bg-opacity-10 text-success small">v{{ prompt.currentVersion }}</span>
            </div>
            <p *ngIf="prompt.description" class="text-muted small mb-1">{{ prompt.description }}</p>
            <p class="text-muted small mb-0 prompt-preview">{{ truncate(prompt.promptValue) }}</p>
          </div>
          <div class="d-flex gap-1 flex-shrink-0">
            <button class="btn btn-sm btn-outline-primary"
                    [attr.title]="'KAIPrompts.edit' | translate"
                    (click)="openEdit(prompt)">
              <i class="bi bi-pencil"></i>
            </button>
            <button class="btn btn-sm btn-outline-info"
                    [attr.title]="'KAIPrompts.test' | translate"
                    (click)="openTest(prompt)">
              <i class="bi bi-play"></i>
            </button>
            <button class="btn btn-sm btn-outline-secondary"
                    [attr.title]="'KAIPrompts.history' | translate"
                    (click)="openHistory(prompt)">
              <i class="bi bi-clock-history"></i>
            </button>
            <button class="btn btn-sm btn-outline-warning"
                    [attr.title]="'KAIPrompts.reset' | translate"
                    (click)="openResetModal(prompt)">
              <i class="bi bi-arrow-counterclockwise"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div *ngIf="filteredPrompts.length === 0 && !loading" class="text-center py-5 text-muted">
      <i class="bi bi-search fs-1 d-block mb-2"></i>
      {{ 'KAIPrompts.noResults' | translate }}
    </div>
  </div>
</div>

<!-- ═══════════════════════════════════════════ -->
<!-- Edit Drawer (NgbOffcanvas)                  -->
<!-- ═══════════════════════════════════════════ -->
<ng-template #editDrawer let-offcanvas>
  <div class="offcanvas-header">
    <h5 class="offcanvas-title">
      <i class="bi bi-pencil me-2"></i>{{ getShortKey(selectedPrompt?.promptKey || '') }}
    </h5>
    <button type="button" class="btn-close" (click)="cancelEdit()"></button>
  </div>
  <div class="offcanvas-body d-flex flex-column">
    <div class="flex-grow-1">
      <!-- Info -->
      <div class="mb-3">
        <div class="d-flex gap-2 mb-2">
          <span class="badge badge-soft-dark bg-opacity-10 text-dark">{{ selectedPrompt?.module }}</span>
          <span *ngIf="selectedPrompt?.outputFormat" class="badge" [ngClass]="getOutputFormatBadgeClass(selectedPrompt?.outputFormat || '')">
            {{ selectedPrompt?.outputFormat }}
          </span>
          <span class="badge badge-soft-success bg-opacity-10 text-success">v{{ selectedPrompt?.currentVersion }}</span>
        </div>
        <p *ngIf="selectedPrompt?.description" class="text-muted small">{{ selectedPrompt?.description }}</p>
      </div>

      <!-- Constraints -->
      <div class="mb-3">
        <label class="form-label fw-semibold">{{ 'KAIPrompts.constraints' | translate }}</label>
        <textarea class="form-control font-monospace small" rows="4"
                  [(ngModel)]="editConstraints"
                  [placeholder]="'KAIPrompts.constraintsPlaceholder' | translate"></textarea>
        <div class="form-text">{{ 'KAIPrompts.constraintsHelp' | translate }}</div>
      </div>

      <!-- Prompt Value -->
      <div class="mb-3">
        <label class="form-label fw-semibold">{{ 'KAIPrompts.promptValue' | translate }}</label>
        <textarea class="form-control font-monospace small" rows="12"
                  [(ngModel)]="editValue"
                  [placeholder]="'KAIPrompts.promptValuePlaceholder' | translate"></textarea>
      </div>

      <!-- Change Note -->
      <div class="mb-3">
        <label class="form-label fw-semibold">{{ 'KAIPrompts.changeNote' | translate }}</label>
        <input type="text" class="form-control"
               [(ngModel)]="editChangeNote"
               [placeholder]="'KAIPrompts.changeNotePlaceholder' | translate" />
      </div>
    </div>

    <!-- Actions -->
    <div class="d-flex gap-2 pt-3 border-top">
      <button class="btn btn-sm btn-outline-info" (click)="openWorkshop(selectedPrompt!)" [disabled]="!selectedPrompt">
        <i class="bi bi-magic me-1"></i>{{ 'KAIPrompts.workshop' | translate }}
      </button>
      <button class="btn btn-sm btn-outline-secondary" (click)="openTest(selectedPrompt!)" [disabled]="!selectedPrompt">
        <i class="bi bi-play me-1"></i>{{ 'KAIPrompts.test' | translate }}
      </button>
      <div class="ms-auto d-flex gap-2">
        <button class="btn btn-secondary" (click)="cancelEdit()">
          {{ 'General.cancel' | translate }}
        </button>
        <button class="btn btn-primary" (click)="savePrompt()" [disabled]="saving">
          <span *ngIf="saving" class="spinner-border spinner-border-sm me-1"></span>
          {{ 'General.save' | translate }}
        </button>
      </div>
    </div>
  </div>
</ng-template>

<!-- ═══════════════════════════════════════════ -->
<!-- Version History Modal                       -->
<!-- ═══════════════════════════════════════════ -->
<ng-template #historyModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">
      <i class="bi bi-clock-history me-2"></i>
      {{ 'KAIPrompts.historyTitle' | translate }}: {{ getShortKey(selectedPrompt?.promptKey || '') }}
    </h5>
    <button type="button" class="btn-close" (click)="closeHistory()"></button>
  </div>
  <div class="modal-body">
    <div *ngIf="historyLoading" class="text-center py-3">
      <div class="spinner-border spinner-border-sm text-primary"></div>
    </div>
    <div *ngIf="!historyLoading && versionHistory.length === 0" class="text-center text-muted py-3">
      {{ 'KAIPrompts.noHistory' | translate }}
    </div>
    <div *ngFor="let version of versionHistory" class="border rounded p-2 mb-2">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <div>
          <span class="badge badge-soft-success bg-opacity-10 text-success me-1">v{{ version.versionNumber }}</span>
          <small class="text-muted">{{ version.changedDate | date:'dd.MM.yyyy HH:mm' }}</small>
          <small *ngIf="version.changedByName" class="text-muted ms-2">
            <i class="bi bi-person"></i> {{ version.changedByName }}
          </small>
        </div>
        <button *ngIf="version.versionNumber !== selectedPrompt?.currentVersion"
                class="btn btn-sm btn-outline-warning"
                (click)="rollbackToVersion(version)">
          <i class="bi bi-arrow-counterclockwise me-1"></i>{{ 'KAIPrompts.rollback' | translate }}
        </button>
        <span *ngIf="version.versionNumber === selectedPrompt?.currentVersion" class="badge badge-soft-primary">
          {{ 'KAIPrompts.current' | translate }}
        </span>
      </div>
      <p *ngIf="version.changeNote" class="small text-muted mb-1">
        <i class="bi bi-sticky"></i> {{ version.changeNote }}
      </p>
      <pre class="small mb-0 prompt-preview-block">{{ truncate(version.promptValue, 300) }}</pre>
    </div>
  </div>
  <div class="modal-footer">
    <button class="btn btn-secondary" (click)="closeHistory()">{{ 'General.close' | translate }}</button>
  </div>
</ng-template>

<!-- ═══════════════════════════════════════════ -->
<!-- Test Prompt Modal                           -->
<!-- ═══════════════════════════════════════════ -->
<ng-template #testModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">
      <i class="bi bi-play me-2"></i>
      {{ 'KAIPrompts.testTitle' | translate }}: {{ getShortKey(selectedPrompt?.promptKey || '') }}
    </h5>
    <button type="button" class="btn-close" (click)="closeTest()"></button>
  </div>
  <div class="modal-body">
    <div class="mb-3">
      <label class="form-label fw-semibold">{{ 'KAIPrompts.systemPromptPreview' | translate }}</label>
      <pre class="small bg-light p-2 rounded prompt-preview-block">{{ truncate(selectedPrompt?.promptValue || '', 400) }}</pre>
    </div>
    <div class="mb-3">
      <label class="form-label fw-semibold">{{ 'KAIPrompts.testMessage' | translate }}</label>
      <textarea class="form-control" rows="3"
                [(ngModel)]="testMessage"
                [placeholder]="'KAIPrompts.testMessagePlaceholder' | translate"></textarea>
    </div>
    <button class="btn btn-primary mb-3" (click)="runTest()" [disabled]="testing || !testMessage.trim()">
      <span *ngIf="testing" class="spinner-border spinner-border-sm me-1"></span>
      <i *ngIf="!testing" class="bi bi-send me-1"></i>
      {{ 'KAIPrompts.runTest' | translate }}
    </button>
    <div *ngIf="testResult" class="border rounded p-3">
      <label class="form-label fw-semibold">{{ 'KAIPrompts.testResult' | translate }}</label>
      <pre class="small mb-2 prompt-preview-block">{{ testResult }}</pre>
      <div class="d-flex gap-3 text-muted small">
        <span><i class="bi bi-hash"></i> {{ testTokens }} tokens</span>
        <span><i class="bi bi-currency-dollar"></i> {{ testCost | number:'1.4-4' }} NOK</span>
      </div>
    </div>
  </div>
  <div class="modal-footer">
    <button class="btn btn-secondary" (click)="closeTest()">{{ 'General.close' | translate }}</button>
  </div>
</ng-template>

<!-- ═══════════════════════════════════════════ -->
<!-- Workshop Modal                              -->
<!-- ═══════════════════════════════════════════ -->
<ng-template #workshopModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">
      <i class="bi bi-magic me-2"></i>
      {{ 'KAIPrompts.workshopTitle' | translate }}
    </h5>
    <button type="button" class="btn-close" (click)="closeWorkshop()"></button>
  </div>
  <div class="modal-body">
    <div class="mb-3">
      <label class="form-label fw-semibold">{{ 'KAIPrompts.workshopOperation' | translate }}</label>
      <select class="form-select" [(ngModel)]="workshopOperation">
        <option value="validate">{{ 'KAIPrompts.workshopValidate' | translate }}</option>
        <option value="improve">{{ 'KAIPrompts.workshopImprove' | translate }}</option>
        <option value="generate">{{ 'KAIPrompts.workshopGenerate' | translate }}</option>
      </select>
    </div>
    <div class="mb-3">
      <label class="form-label fw-semibold">{{ 'KAIPrompts.workshopContext' | translate }}</label>
      <textarea class="form-control" rows="3"
                [(ngModel)]="workshopContext"
                [placeholder]="'KAIPrompts.workshopContextPlaceholder' | translate"></textarea>
    </div>
    <button class="btn btn-primary mb-3" (click)="runWorkshop()" [disabled]="workshopLoading">
      <span *ngIf="workshopLoading" class="spinner-border spinner-border-sm me-1"></span>
      <i *ngIf="!workshopLoading" class="bi bi-magic me-1"></i>
      {{ 'KAIPrompts.runWorkshop' | translate }}
    </button>
    <div *ngIf="workshopResult" class="border rounded p-3">
      <label class="form-label fw-semibold">{{ 'KAIPrompts.workshopResult' | translate }}</label>
      <pre class="small mb-2 prompt-preview-block">{{ workshopResult }}</pre>
      <div class="d-flex gap-2 small text-muted mb-2">
        <span><i class="bi bi-hash"></i> {{ workshopTokens }} tokens</span>
      </div>
      <button class="btn btn-sm btn-success" (click)="applyWorkshopResult()">
        <i class="bi bi-check-lg me-1"></i>{{ 'KAIPrompts.applyResult' | translate }}
      </button>
    </div>
  </div>
  <div class="modal-footer">
    <button class="btn btn-secondary" (click)="closeWorkshop()">{{ 'General.close' | translate }}</button>
  </div>
</ng-template>

<!-- ═══════════════════════════════════════════ -->
<!-- Reset Confirm Modal                         -->
<!-- ═══════════════════════════════════════════ -->
<ng-template #resetConfirmModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">
      <i class="bi bi-exclamation-triangle text-warning me-2"></i>
      {{ 'KAIPrompts.resetConfirmTitle' | translate }}
    </h5>
    <button type="button" class="btn-close" (click)="cancelReset()"></button>
  </div>
  <div class="modal-body">
    <p>{{ 'KAIPrompts.resetConfirmMessage' | translate }}</p>
    <p class="fw-bold">{{ getShortKey(promptToReset?.promptKey || '') }}</p>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="cancelReset()">
      {{ 'General.cancel' | translate }}
    </button>
    <button type="button" class="btn btn-warning" (click)="confirmReset()">
      <i class="bi bi-arrow-counterclockwise me-1"></i>{{ 'KAIPrompts.reset' | translate }}
    </button>
  </div>
</ng-template>
